import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Search, Edit3, Trash2, Star, TrendingUp, Calendar, DollarSign, Target, Award } from 'lucide-react';
import { questionBankService, QuestionBank as FirebaseQuestionBank } from '../services/questionBankService';
import { getCurrentUser } from '../services/firebase';

// Basit soru bankası arayüzü (Firebase ile uyumlu)
interface QuestionBank {
  id: string;
  name: string;
  subject: string;
  publisher: string;
  totalQuestions: number;
  completedQuestions: number;
  price: number;
  isFavorite: boolean;
  color: string;
  borderColor: string;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
}

// 8. sınıf LGS dersleri (Resmi ders adları)
const LGS_SUBJECTS = [
  { key: 'turkce', name: 'Türkçe', color: 'bg-red-500' },
  { key: 'matematik', name: 'Matematik', color: 'bg-blue-500' },
  { key: 'fen', name: 'Fen Bilimleri', color: 'bg-green-500' },
  { key: 'inkilap', name: 'T.C. İnkılâp Tarihi ve Atatürkçülük', color: 'bg-purple-500' },
  { key: 'yabanci_dil', name: 'Yabancı Dil', color: 'bg-yellow-500' },
  { key: 'din', name: 'Din Kültürü ve Ahlak Bilgisi', color: 'bg-indigo-500' }
];

// Popüler yayınevleri
const PUBLISHERS = [
  'Karekök Yayınları',
  'Palme Yayınları', 
  'Bilfen Yayınları',
  'Tudem Yayınları',
  'Hız Yayınları',
  'Editör Yayınları',
  'Diğer'
];

// Kitap kenarlık renk seçenekleri
const BORDER_COLORS = [
  { name: 'Altın', value: 'border-yellow-400', class: 'bg-yellow-400' },
  { name: 'Gümüş', value: 'border-gray-400', class: 'bg-gray-400' },
  { name: 'Bronz', value: 'border-amber-600', class: 'bg-amber-600' },
  { name: 'Mavi', value: 'border-blue-500', class: 'bg-blue-500' },
  { name: 'Yeşil', value: 'border-green-500', class: 'bg-green-500' },
  { name: 'Kırmızı', value: 'border-red-500', class: 'bg-red-500' },
  { name: 'Mor', value: 'border-purple-500', class: 'bg-purple-500' },
  { name: 'Pembe', value: 'border-pink-500', class: 'bg-pink-500' }
];

const QuestionBank: React.FC = () => {
  const [questionBanks, setQuestionBanks] = useState<QuestionBank[]>([]);
  const [filteredBanks, setFilteredBanks] = useState<QuestionBank[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('Tümü');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBank, setEditingBank] = useState<QuestionBank | null>(null);
  
  // Yeni soru bankası formu
  const [newBank, setNewBank] = useState({
    name: '',
    subject: 'turkce',
    publisher: 'Karekök Yayınları',
    totalQuestions: '',
    price: '',
    borderColor: 'border-yellow-400'
  });
  
  // Özel yayın evi adı için state
  const [customPublisher, setCustomPublisher] = useState('');

  // Firebase'den veri yükleme
  useEffect(() => {
    const loadQuestionBanks = async () => {
      try {
        setLoading(true);
        const user = getCurrentUser();
        if (user) {
          const banks = await questionBankService.getQuestionBanks(user.uid);
          const mappedBanks: QuestionBank[] = banks.map(bank => ({
            id: bank.id,
            name: bank.name,
            subject: bank.subject,
            publisher: bank.publisher,
            totalQuestions: bank.totalQuestions || 0,
            completedQuestions: bank.completedQuestions || 0,
            price: bank.price,
            isFavorite: bank.isFavorite || false,
            color: LGS_SUBJECTS.find(s => s.key === bank.subject)?.color || 'bg-gray-500',
            borderColor: (bank as any).borderColor || 'border-yellow-400',
            userId: bank.userId,
            createdAt: bank.createdAt,
            updatedAt: bank.updatedAt
          }));
          setQuestionBanks(mappedBanks);
          setFilteredBanks(mappedBanks);
        }
      } catch (err) {
        setError('Soru bankaları yüklenirken hata oluştu');
        console.error('Error loading question banks:', err);
      } finally {
        setLoading(false);
      }
    };

    loadQuestionBanks();
  }, []);

  // Filtreleme ve arama
  useEffect(() => {
    let filtered = questionBanks;
    
    // Ders filtresi
    if (selectedSubject !== 'Tümü') {
      filtered = filtered.filter(bank => bank.subject === selectedSubject);
    }
    
    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(bank => 
        bank.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bank.publisher.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredBanks(filtered);
  }, [questionBanks, selectedSubject, searchTerm]);

  // Yeni soru bankası ekleme
  const handleAddBank = async () => {
    if (!newBank.name || !newBank.totalQuestions || !newBank.price) {
      alert('Lütfen tüm alanları doldurun!');
      return;
    }
    
    // Diğer seçildiğinde özel yayın evi adı kontrolü
    if (newBank.publisher === 'Diğer' && !customPublisher.trim()) {
      alert('Lütfen yayın evi adını girin!');
      return;
    }

    try {
      const user = getCurrentUser();
      if (!user) {
        alert('Lütfen giriş yapın!');
        return;
      }

      const subjectData = LGS_SUBJECTS.find(s => s.key === newBank.subject);
      
      const bankData: Omit<FirebaseQuestionBank, 'id'> = {
        name: newBank.name,
        subject: newBank.subject,
        publisher: newBank.publisher === 'Diğer' ? customPublisher : newBank.publisher,
        totalQuestions: parseInt(newBank.totalQuestions),
        completedQuestions: 0,
        price: parseFloat(newBank.price),
        isFavorite: false,
        userId: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        borderColor: newBank.borderColor
      } as any;
      
      const newBankId = await questionBankService.addQuestionBank(bankData);
      
      const bank: QuestionBank = {
        id: newBankId,
        ...bankData,
        color: subjectData?.color || 'bg-gray-500'
      };
      
      setQuestionBanks([...questionBanks, bank]);
      setNewBank({
        name: '',
        subject: 'turkce',
        publisher: 'Karekök Yayınları',
        totalQuestions: '',
        price: '',
        borderColor: 'border-yellow-400'
      });
      setCustomPublisher('');
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding question bank:', error);
      alert('Soru bankası eklenirken hata oluştu!');
    }
  };

  // Soru bankası silme
  const handleDeleteBank = async (id: string) => {
    if (window.confirm('Bu soru bankasını silmek istediğinizden emin misiniz?')) {
      try {
        await questionBankService.deleteQuestionBank(id);
        setQuestionBanks(questionBanks.filter(bank => bank.id !== id));
      } catch (error) {
        console.error('Error deleting question bank:', error);
        alert('Soru bankası silinirken hata oluştu!');
      }
    }
  };

  // Soru bankası düzenleme
  const handleEditBank = async () => {
    if (!editingBank || !newBank.name || !newBank.totalQuestions || !newBank.price) {
      alert('Lütfen tüm alanları doldurun!');
      return;
    }
    
    // Diğer seçildiğinde özel yayın evi adı kontrolü
    if (newBank.publisher === 'Diğer' && !customPublisher.trim()) {
      alert('Lütfen yayın evi adını girin!');
      return;
    }

    try {
      const subjectData = LGS_SUBJECTS.find(s => s.key === newBank.subject);
      
      const updatedData: Partial<FirebaseQuestionBank> = {
        name: newBank.name,
        subject: newBank.subject,
        publisher: newBank.publisher === 'Diğer' ? customPublisher : newBank.publisher,
        totalQuestions: parseInt(newBank.totalQuestions),
        price: parseFloat(newBank.price),
        updatedAt: new Date().toISOString(),
        borderColor: newBank.borderColor
      } as any;
      
      await questionBankService.updateQuestionBank(editingBank.id, updatedData);
      
      const updatedBank: QuestionBank = {
        ...editingBank,
        ...updatedData,
        color: subjectData?.color || 'bg-gray-500'
      };
      
      setQuestionBanks(questionBanks.map(bank => 
        bank.id === editingBank.id ? updatedBank : bank
      ));
      
      setNewBank({
        name: '',
        subject: 'turkce',
        publisher: 'Karekök Yayınları',
        totalQuestions: '',
        price: '',
        borderColor: 'border-yellow-400'
      });
      setCustomPublisher('');
      setEditingBank(null);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error updating question bank:', error);
      alert('Soru bankası güncellenirken hata oluştu!');
    }
  };

  // Favorilere ekleme/çıkarma
  const toggleFavorite = async (id: string) => {
    try {
      const bank = questionBanks.find(b => b.id === id);
      if (!bank) return;
      
      const updatedData = {
        isFavorite: !bank.isFavorite,
        updatedAt: new Date().toISOString()
      };
      
      await questionBankService.updateQuestionBank(id, updatedData);
      
      setQuestionBanks(questionBanks.map(bank => 
        bank.id === id ? { ...bank, ...updatedData } : bank
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Favori durumu güncellenirken hata oluştu!');
    }
  };

  // İlerleme güncelleme
  const updateProgress = async (id: string, completedQuestions: number) => {
    try {
      const updatedData = {
        completedQuestions,
        updatedAt: new Date().toISOString()
      };
      
      await questionBankService.updateQuestionBank(id, updatedData);
      
      setQuestionBanks(questionBanks.map(bank => 
        bank.id === id ? { ...bank, ...updatedData } : bank
      ));
    } catch (error) {
      console.error('Error updating progress:', error);
      alert('İlerleme güncellenirken hata oluştu!');
    }
  };

  // Ders adını getir
  const getSubjectName = (subjectKey: string) => {
    return LGS_SUBJECTS.find(s => s.key === subjectKey)?.name || subjectKey;
  };

  return (
    <div className="min-h-screen bg-transparent p-6">
      <div className="max-w-7xl mx-auto">
        {/* Modern Header - ExamResults Tarzı */}
        <div className="mb-12">
          <div className="relative overflow-hidden bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 rounded-3xl p-8 text-white">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent">
                    Soru Bankalarım
                  </h1>
                  <p className="text-purple-100 text-lg">
                    Modern tasarımla soru bankalarınızı yönetin ve başarınızı takip edin
                  </p>
                </div>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="group relative overflow-hidden bg-white/20 backdrop-blur-sm border border-white/30 text-white px-6 py-3 rounded-2xl hover:bg-white/30 transition-all duration-300 transform hover:scale-105 mt-4 md:mt-0 flex items-center gap-2"
                >
                  <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                  Yeni Kitap Ekle
                </button>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-white/5 to-transparent rounded-full translate-y-24 -translate-x-24"></div>
          </div>
        </div>

        {/* Modern Filters - ExamResults Tarzı */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-6 mb-6 shadow-xl border border-white/20">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Kitap adı veya yayınevi ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white backdrop-blur-sm"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white backdrop-blur-sm"
              >
                <option value="Tümü">Tüm Dersler</option>
                {LGS_SUBJECTS.map(subject => (
                  <option key={subject.key} value={subject.key}>{subject.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

          {/* Glassmorphism Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12 px-4 sm:px-6">
            <div className="bg-white/20 dark:bg-white/5 rounded-xl p-4 sm:p-6 hover:bg-white/30 dark:hover:bg-white/10 transition-all duration-300 group shadow-lg hover:shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Toplam Kitap</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{questionBanks.length}</p>
                </div>
                <div className="p-2 sm:p-3 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
            </div>
            
            <div className="bg-white/20 dark:bg-white/5 rounded-xl p-4 sm:p-6 hover:bg-white/30 dark:hover:bg-white/10 transition-all duration-300 group shadow-lg hover:shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Toplam Soru</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{questionBanks.reduce((acc, bank) => acc + bank.totalQuestions, 0)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-gradient-to-br from-green-400 to-green-600 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <Target className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
            </div>
            
            <div className="bg-white/20 dark:bg-white/5 rounded-xl p-4 sm:p-6 hover:bg-white/30 dark:hover:bg-white/10 transition-all duration-300 group shadow-lg hover:shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Çözülen Soru</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{questionBanks.reduce((acc, bank) => acc + bank.completedQuestions, 0)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
            </div>
            
            <div className="bg-white/20 dark:bg-white/5 rounded-xl p-4 sm:p-6 hover:bg-white/30 dark:hover:bg-white/10 transition-all duration-300 group shadow-lg hover:shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Toplam Harcama</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">₺{questionBanks.reduce((acc, bank) => acc + bank.price, 0).toFixed(2)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
            </div>
          </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">Soru bankaları yükleniyor...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* Modern Glassmorphism Kitap Kartları */}
        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6 px-4 sm:px-6 lg:px-8 py-6 pb-12">
            {filteredBanks.map((bank) => {
              const progress = (bank.completedQuestions / bank.totalQuestions) * 100;
              const subjectInfo = LGS_SUBJECTS.find(s => s.key === bank.subject);
              const borderColorInfo = BORDER_COLORS.find(c => c.value === bank.borderColor);
              const spineColorClass = borderColorInfo ? borderColorInfo.class.replace('bg-', '') : 'gray-400';
              
              return (
                <div key={bank.id} className="group relative">
                  {/* Modern Glassmorphism Kitap Kartı */}
                  <div className={`relative bg-white/20 dark:bg-white/5 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-4 hover:scale-105 overflow-hidden border-2 ${bank.borderColor}`}>

                    
                    {/* Kitap Sırtı - Modern */}
                    <div className={`absolute left-0 top-0 bottom-0 w-5 bg-gradient-to-b from-${spineColorClass} via-${spineColorClass} to-${spineColorClass} rounded-l-3xl shadow-2xl`} style={{background: `linear-gradient(to bottom, ${borderColorInfo?.class.includes('yellow') ? '#facc15' : borderColorInfo?.class.includes('gray') ? '#9ca3af' : borderColorInfo?.class.includes('amber') ? '#d97706' : borderColorInfo?.class.includes('blue') ? '#3b82f6' : borderColorInfo?.class.includes('green') ? '#22c55e' : borderColorInfo?.class.includes('red') ? '#ef4444' : borderColorInfo?.class.includes('purple') ? '#a855f7' : borderColorInfo?.class.includes('pink') ? '#ec4899' : '#9ca3af'}, ${borderColorInfo?.class.includes('yellow') ? '#eab308' : borderColorInfo?.class.includes('gray') ? '#6b7280' : borderColorInfo?.class.includes('amber') ? '#b45309' : borderColorInfo?.class.includes('blue') ? '#2563eb' : borderColorInfo?.class.includes('green') ? '#16a34a' : borderColorInfo?.class.includes('red') ? '#dc2626' : borderColorInfo?.class.includes('purple') ? '#9333ea' : borderColorInfo?.class.includes('pink') ? '#db2777' : '#6b7280'}, ${borderColorInfo?.class.includes('yellow') ? '#ca8a04' : borderColorInfo?.class.includes('gray') ? '#4b5563' : borderColorInfo?.class.includes('amber') ? '#92400e' : borderColorInfo?.class.includes('blue') ? '#1d4ed8' : borderColorInfo?.class.includes('green') ? '#15803d' : borderColorInfo?.class.includes('red') ? '#b91c1c' : borderColorInfo?.class.includes('purple') ? '#7c3aed' : borderColorInfo?.class.includes('pink') ? '#be185d' : '#4b5563'})`}}>
                      <div className="h-full flex flex-col items-center justify-center space-y-1">
                        <div className="w-1 h-6 bg-white/40 rounded-full"></div>
                        <div className="w-0.5 h-4 bg-white/20 rounded-full"></div>
                      </div>
                    </div>

                    {/* Floating Fiyat Etiketi */}
                    <div className="absolute top-2 right-2 z-20">
                      <div className="backdrop-blur-md bg-gradient-to-r from-green-400 to-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg border border-white/20">
                        ₺{bank.price.toFixed(2)}
                      </div>
                    </div>



                    {/* Kitap İçeriği */}
                    <div className="pl-6 pr-3 py-3">
                      {/* Başlık ve Ders */}
                      <div className="mb-2">
                        <h3 className="font-bold text-gray-900 dark:text-white text-sm mb-1 line-clamp-2 leading-tight transition-colors duration-300">
                          {bank.name}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 font-semibold text-xs mb-1">
                          {subjectInfo?.name || bank.subject}
                        </p>
                        <p className="text-gray-600 dark:text-gray-300 text-xs opacity-80">
                          {bank.publisher}
                        </p>
                      </div>

                      {/* Modern İlerleme Bölümü */}
                      <div className="mb-3 space-y-2">
                        {/* Gelişmiş İlerleme Çubuğu */}
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                              İlerleme
                            </span>
                            <span className="text-xs font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                              {Math.round(progress)}%
                            </span>
                          </div>
                          <div className="relative">
                            <div className="w-full bg-gray-200/50 dark:bg-white/10 rounded-full h-2 overflow-hidden backdrop-blur-sm border border-gray-300/50 dark:border-white/20">
                              <div 
                                className="h-full bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 rounded-full transition-all duration-700 ease-out shadow-lg relative overflow-hidden"
                                style={{ width: `${progress}%` }}
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Floating Action Buttons */}
                        <div className="flex gap-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingBank(bank);
                              setNewBank({
                                name: bank.name,
                                subject: bank.subject,
                                publisher: bank.publisher,
                                totalQuestions: bank.totalQuestions.toString(),
                                price: bank.price.toString(),
                                borderColor: bank.borderColor || 'border-yellow-400'
                              });
                              setShowAddModal(true);
                            }}
                            className="flex-1 p-3 backdrop-blur-md bg-blue-500/20 border border-blue-400/30 text-blue-600 dark:text-blue-200 rounded-xl hover:bg-blue-500/30 hover:border-blue-400/50 transition-all duration-300 flex items-center justify-center group/btn"
                          >
                            <Edit3 className="w-4 h-4 group-hover/btn:scale-110 transition-transform duration-200" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteBank(bank.id);
                            }}
                            className="flex-1 p-3 backdrop-blur-md bg-red-500/20 border border-red-400/30 text-red-600 dark:text-red-200 rounded-xl hover:bg-red-500/30 hover:border-red-400/50 transition-all duration-300 flex items-center justify-center group/btn"
                          >
                            <Trash2 className="w-4 h-4 group-hover/btn:scale-110 transition-transform duration-200" />
                          </button>
                        </div>

                        {/* Sayfa Numarası Göstergesi */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-200">
                              Hangi Sayfadasın?
                            </label>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (bank.completedQuestions > 0) {
                                  updateProgress(bank.id, bank.completedQuestions - 1);
                                }
                              }}
                              className="w-6 h-6 bg-gray-200/50 dark:bg-white/10 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-300/50 dark:hover:bg-white/20 transition-all duration-200"
                            >
                              -
                            </button>
                            <div className="flex-1 text-center">
                              <span className="text-sm font-bold text-gray-800 dark:text-white bg-white/20 dark:bg-white/10 px-3 py-1 rounded-lg">
                                {bank.completedQuestions} / {bank.totalQuestions}
                              </span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (bank.completedQuestions < bank.totalQuestions) {
                                  updateProgress(bank.id, bank.completedQuestions + 1);
                                }
                              }}
                              className="w-6 h-6 bg-gray-200/50 dark:bg-white/10 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-300/50 dark:hover:bg-white/20 transition-all duration-200"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Modern Alt Bilgiler */}
                      <div className="backdrop-blur-sm bg-gray-100/50 dark:bg-white/5 rounded-xl p-3 border border-gray-300/30 dark:border-white/10">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-300">Toplam Sayfa:</span>
                          <span className="text-sm font-bold text-gray-800 dark:text-white">{bank.totalQuestions}</span>
                        </div>
                      </div>
                    </div>


                  </div>
                </div>
              );
            })}
          </div>
        )}

          {/* Boş Durum */}
          {filteredBanks.length === 0 && (
            <div className="text-center py-12 sm:py-20">
              <div className="backdrop-blur-lg bg-gray-100/50 dark:bg-white/10 rounded-2xl border border-gray-300/30 dark:border-white/20 p-8 sm:p-12 max-w-md mx-auto">
                <BookOpen className="w-16 h-16 sm:w-20 sm:h-20 text-gray-500 dark:text-gray-400 mx-auto mb-4 sm:mb-6" />
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white mb-3 sm:mb-4">Soru bankası bulunamadı</h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-6 sm:mb-8">Arama kriterlerinizi değiştirin veya yeni bir soru bankası ekleyin.</p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-2xl transform hover:scale-105 text-sm sm:text-base"
                >
                  İlk Soru Bankasını Ekle
                </button>
              </div>
            </div>
          )}
        </div>

      {/* Modern Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="backdrop-blur-lg bg-white/10 border border-white/20 rounded-2xl p-4 sm:p-8 w-full max-w-md max-h-[90vh] overflow-y-auto scrollbar-hide" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 text-center">
              {editingBank ? 'Soru Bankasını Düzenle' : 'Yeni Soru Bankası Ekle'}
            </h3>
            
            <div className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Kitap Adı</label>
                <input
                  type="text"
                  value={newBank.name}
                  onChange={(e) => setNewBank({...newBank, name: e.target.value})}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 backdrop-blur-sm text-sm sm:text-base"
                  placeholder="Örn: LGS Matematik Soru Bankası"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Ders</label>
                <select
                  value={newBank.subject}
                  onChange={(e) => setNewBank({...newBank, subject: e.target.value})}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 backdrop-blur-sm text-sm sm:text-base"
                >
                  {LGS_SUBJECTS.map(subject => (
                    <option key={subject.key} value={subject.key} className="bg-gray-800">{subject.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Yayınevi</label>
                <select
                  value={newBank.publisher}
                  onChange={(e) => {
                    setNewBank({...newBank, publisher: e.target.value});
                    if (e.target.value !== 'Diğer') {
                      setCustomPublisher('');
                    }
                  }}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 backdrop-blur-sm text-sm sm:text-base"
                >
                  {PUBLISHERS.map(publisher => (
                    <option key={publisher} value={publisher} className="bg-gray-800">{publisher}</option>
                  ))}
                </select>
                
                {/* Özel yayın evi input alanı */}
                {newBank.publisher === 'Diğer' && (
                  <div className="mt-3">
                    <input
                      type="text"
                      value={customPublisher}
                      onChange={(e) => setCustomPublisher(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 backdrop-blur-sm text-sm sm:text-base"
                      placeholder="Yayın evi adını yazın..."
                    />
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Toplam Soru Sayısı</label>
                <input
                  type="number"
                  value={newBank.totalQuestions}
                  onChange={(e) => setNewBank({...newBank, totalQuestions: e.target.value})}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 backdrop-blur-sm text-sm sm:text-base"
                  placeholder="Örn: 300"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Fiyat (₺)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newBank.price}
                  onChange={(e) => setNewBank({...newBank, price: e.target.value})}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 backdrop-blur-sm text-sm sm:text-base"
                  placeholder="Örn: 45.90"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Kitap Kenarlık Rengi</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                  {BORDER_COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setNewBank({...newBank, borderColor: color.value})}
                      className={`relative p-2 sm:p-3 rounded-xl border-2 transition-all duration-300 ${
                        newBank.borderColor === color.value 
                          ? 'border-white shadow-lg scale-105' 
                          : 'border-white/20 hover:border-white/40'
                      }`}
                    >
                      <div className={`w-full h-6 sm:h-8 rounded-lg ${color.class}`}></div>
                      <span className="text-xs text-white mt-1 block">{color.name}</span>
                      {newBank.borderColor === color.value && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setCustomPublisher('');
                  setEditingBank(null);
                  setNewBank({
                    name: '',
                    subject: 'turkce',
                    publisher: 'Karekök Yayınları',
                    totalQuestions: '',
                    price: '',
                    borderColor: 'border-yellow-400'
                  });
                }}
                className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all duration-300 font-medium backdrop-blur-sm text-sm sm:text-base"
              >
                İptal
              </button>
              <button
                onClick={editingBank ? handleEditBank : handleAddBank}
                className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-2xl transform hover:scale-105 text-sm sm:text-base"
              >
                {editingBank ? 'Güncelle' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionBank;
